import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation } from '@/hooks/useApiMutation';
import { fetchServiceAreas } from '@/hooks/service-areas/useServiceAreas';
import { apiDelete, apiPost, operationsClient } from '@/lib/api/client';
import { serviceAreaEndpoints, taxZoneEndpoints } from '@/lib/api/endpoints';
import { serviceAreaKeys, taxGroupKeys, taxZoneKeys } from '@/lib/queryKeys';

export interface BulkDeleteTaxZonesInput {
  stateCode: string;
  county?: string | null;
}

export interface BulkDeleteTaxZonesResult {
  deleted: true;
  scope: 'state' | 'county';
  stateCode: string;
  county: string | null;
  deletedCount: number;
  deletedTaxGroupCount: number;
  zipCodes: string[];
  serviceAreaCleanupFailed?: boolean;
}

function normalizePostalCode(postalCode: string) {
  return postalCode.trim().toUpperCase().replace(/\s/g, '');
}

export function useBulkDeleteTaxZonesWithArea() {
  const queryClient = useQueryClient();

  return useApiMutation<BulkDeleteTaxZonesResult, BulkDeleteTaxZonesInput>({
    mutationFn: async (scope) => {
      const result = await apiPost<BulkDeleteTaxZonesResult, BulkDeleteTaxZonesInput>(
        operationsClient,
        taxZoneEndpoints.bulkDelete(),
        scope,
      );

      if (result.zipCodes.length === 0) {
        return result;
      }

      try {
        const serviceAreas = await queryClient.fetchQuery({
          queryKey: serviceAreaKeys.list(),
          queryFn: fetchServiceAreas,
        });
        const deletedPostalCodes = new Set(result.zipCodes.map(normalizePostalCode));
        const areaIds = [
          ...new Set(
            serviceAreas
              .filter(
                (area) => area.id && deletedPostalCodes.has(normalizePostalCode(area.zipCode)),
              )
              .map((area) => area.id),
          ),
        ];

        const deletions = await Promise.allSettled(
          areaIds.map((id) => apiDelete(operationsClient, serviceAreaEndpoints.delete(id))),
        );
        if (deletions.some((deletion) => deletion.status === 'rejected')) {
          return { ...result, serviceAreaCleanupFailed: true };
        }
        return result;
      } catch {
        return { ...result, serviceAreaCleanupFailed: true };
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: taxZoneKeys.all() });
      void queryClient.invalidateQueries({ queryKey: taxGroupKeys.all() });
      void queryClient.invalidateQueries({ queryKey: serviceAreaKeys.all() });
    },
  });
}
